'use client'

/**
 * Shared pricing/checkout logic for every surface that sells a plan.
 *
 * Extracted from LandingPage so the marketing page and the in-app /mycio/pricing page cannot drift:
 * they must show the SAME tiers at the SAME prices and start the SAME checkout. Duplicating this was the
 * obvious shortcut and exactly how a "$20 here, $240 there" inconsistency gets shipped.
 *
 * Behaviour is lifted verbatim from LandingPage — logged-out goes to sign-in rather than calling checkout
 * unauthenticated, `payments_pending` routes to the holding page, and a beta user gets the confirm before
 * being sent to Stripe.
 */

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useMyCIOAuth } from '@/lib/mycio/MyCIOAuthContext'
import { useMyCIOLanguage, useNumberLocale } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOPlans } from '@/lib/mycio/translations'
import { getMyCIOBillingPagesCopy } from '@/lib/mycio/copy/billingPages'
import { pricingSignInHref } from '@/lib/mycio/pricingIntent'
import { buildPricingPlans } from './pricingPlans'
import { serverErrorText } from '@/lib/mycio/serverText'

export type CheckoutPlan = 'living' | 'plus' | 'wealth' | 'advisor'

export function usePricingSection() {
  const router = useRouter()
  const { session } = useMyCIOAuth()
  const { language } = useMyCIOLanguage()
  const numberLocale = useNumberLocale()

  const [checkoutBusy, setCheckoutBusy] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  // FOUNDER, R2: pricing is UI, so the cards are in the member's language with prices in their notation.
  // Structure and prices live once in content.ts PLANS; words in getMyCIOPlans (see pricingPlans.ts).
  const plans = buildPricingPlans(language, numberLocale)
  const plansCopy = getMyCIOPlans(language)
  const fallbackPlansCopy = getMyCIOPlans('en')
  const checkoutCopy = getMyCIOBillingPagesCopy(language).checkout

  const startCheckout = async (plan: CheckoutPlan) => {
    setCheckoutError(null)
    const token = session?.access_token
    if (!token) {
      router.push(pricingSignInHref(plan))
      return
    }
    setCheckoutBusy(plan)
    try {
      const res = await fetch('/api/mycio/membership/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        url?: string
        detail?: string | { message?: string }
        detail_i18n?: unknown
        error?: string
        beta_converted?: boolean
        payments_pending?: boolean
      }
      if (res.ok && data.payments_pending) {
        router.push('/billing-soon')
        return
      }
      if (res.ok && data.url) {
        if (data.beta_converted) {
          const go = window.confirm(checkoutCopy.betaConfirm)
          if (!go) return
        }
        window.location.href = data.url
        return
      }
      // The backend's sentence in the member's language (detail_i18n), never a raw code or an English
      // transport note; otherwise this page's own "unavailable" line.
      setCheckoutError(serverErrorText(data, language, checkoutCopy.unavailable) ?? checkoutCopy.unavailable)
    } catch {
      setCheckoutError(checkoutCopy.unavailable)
    } finally {
      setCheckoutBusy(null)
    }
  }

  return {
    plans,
    startCheckout,
    checkoutBusy,
    checkoutError,
    plansCopy,
    fallbackPlansCopy,
  }
}
